// ─────────────────────────────────────────────────────────────────────────────
// Party engine — drawing data model (DESIGN-party-engine.md §6)
//
// A drawing is `{ w, h, strokes: [{ c, w, pts: [x, y, …] }] }` in normalized
// 0–1000 coordinates. Resolution independent: it renders anywhere as SVG
// polylines, so there is no image encoding and no canvas-to-blob anywhere in
// the stack. The same object travels over the WebSocket and sits in room
// memory; nothing persists it.
//
// Everything in this module is PURE — no DOM, no React. It is imported by the
// canvas, every renderer, and the tests.
// ─────────────────────────────────────────────────────────────────────────────

/** Edge length of the normalized coordinate space. */
export const DRAW_SPACE = 1000;

/** Points closer than this (normalized units) to the previous kept point are dropped. */
export const MIN_POINT_DISTANCE = 2;

/** Hard cap for a full drawing (design §6). Enforced client-side + server-side. */
export const FULL_DRAWING_MAX_BYTES = 250 * 1024;

/** Hard cap for a mini/avatar-icon drawing (design §2 / §5). */
export const ICON_DRAWING_MAX_BYTES = 10 * 1024;

/**
 * Stroke widths in normalized units — "thin" and "thick" pens.
 *
 * HALVED 2026-07-27 (requirements F7): both pens were ~2× too fat for the draw
 * space and a doodle turned into a blob before it turned into a picture. Two
 * sizes, both half of what they were.
 */
export const STROKE_WIDTHS = [5, 14] as const;

/**
 * Mini/avatar pens. Still chunkier than the full-canvas pens on purpose: an
 * icon drawn at 5 units renders at 0.2px inside a 36px badge, i.e. invisible.
 * Halved alongside the full pens (F7); PlayerBadge's `STROKE_SCALE` is what
 * carries them the rest of the way on the smallest renders.
 */
export const MINI_STROKE_WIDTHS = [20, 45] as const;

export type DrawMode = "full" | "mini";

export interface Stroke {
  /** CSS color (hex). */
  c: string;
  /** Stroke width in normalized units. */
  w: number;
  /** Flat [x0, y0, x1, y1, …] in 0…DRAW_SPACE. */
  pts: number[];
}

export interface Drawing {
  /** Coordinate-space width (always DRAW_SPACE for what this app produces). */
  w: number;
  /** Coordinate-space height. */
  h: number;
  strokes: Stroke[];
}

// ── construction ─────────────────────────────────────────────────────────────

export function emptyDrawing(w: number = DRAW_SPACE, h: number = DRAW_SPACE): Drawing {
  return { w, h, strokes: [] };
}

export function cloneDrawing(d: Drawing): Drawing {
  return { w: d.w, h: d.h, strokes: d.strokes.map((s) => ({ c: s.c, w: s.w, pts: s.pts.slice() })) };
}

/** Clamp + round a raw coordinate into the normalized integer space. */
export function clampCoord(n: number, space: number = DRAW_SPACE): number {
  if (!Number.isFinite(n)) return 0;
  const r = Math.round(n);
  if (r < 0) return 0;
  if (r > space) return space;
  return r;
}

/**
 * Map a client-space point (pointer event) onto the normalized space using the
 * canvas' bounding rect. Kept here (and not in the component) so it is testable.
 */
export function toNormalized(
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  space: number = DRAW_SPACE,
): [number, number] {
  const w = rect.width || 1;
  const h = rect.height || 1;
  return [
    clampCoord(((clientX - rect.left) / w) * space, space),
    clampCoord(((clientY - rect.top) / h) * space, space),
  ];
}

// ── decimation ───────────────────────────────────────────────────────────────

export function pointDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.hypot(x2 - x1, y2 - y1);
}

/** Last point of a stroke, or null when it has none. */
export function lastPointOf(stroke: Stroke): [number, number] | null {
  const n = stroke.pts.length;
  if (n < 2) return null;
  return [stroke.pts[n - 2], stroke.pts[n - 1]];
}

/**
 * Live decimation predicate: true when (x, y) is far enough from the previous
 * kept point to be worth recording. Same rule `decimateStroke` applies in bulk,
 * so a live-drawn stroke is already decimated and re-running decimation is a
 * no-op.
 */
export function shouldAppendPoint(
  stroke: Stroke,
  x: number,
  y: number,
  minDist: number = MIN_POINT_DISTANCE,
): boolean {
  const last = lastPointOf(stroke);
  if (!last) return true;
  return pointDistance(last[0], last[1], x, y) >= minDist;
}

/**
 * Append (x, y) to a stroke IN PLACE when it survives decimation.
 * Returns true when the point was recorded.
 */
export function appendPoint(
  stroke: Stroke,
  x: number,
  y: number,
  minDist: number = MIN_POINT_DISTANCE,
): boolean {
  const cx = clampCoord(x);
  const cy = clampCoord(y);
  if (!shouldAppendPoint(stroke, cx, cy, minDist)) return false;
  stroke.pts.push(cx, cy);
  return true;
}

/** Pure bulk decimation: keep the first point, then every point ≥ minDist from the last kept one. */
export function decimateStroke(stroke: Stroke, minDist: number = MIN_POINT_DISTANCE): Stroke {
  const pts: number[] = [];
  let lx = 0;
  let ly = 0;
  for (let i = 0; i + 1 < stroke.pts.length; i += 2) {
    const x = clampCoord(stroke.pts[i]);
    const y = clampCoord(stroke.pts[i + 1]);
    if (pts.length === 0 || pointDistance(lx, ly, x, y) >= minDist) {
      pts.push(x, y);
      lx = x;
      ly = y;
    }
  }
  return { c: stroke.c, w: stroke.w, pts };
}

/** Decimate every stroke; strokes left with no points are dropped. */
export function decimateDrawing(d: Drawing, minDist: number = MIN_POINT_DISTANCE): Drawing {
  return {
    w: d.w,
    h: d.h,
    strokes: d.strokes.map((s) => decimateStroke(s, minDist)).filter((s) => s.pts.length >= 2),
  };
}

// ── size caps ────────────────────────────────────────────────────────────────

export function serializeDrawing(d: Drawing): string {
  return JSON.stringify(d);
}

const encoder: { encode(s: string): { length: number } } =
  typeof TextEncoder !== "undefined"
    ? new TextEncoder()
    : { encode: (s: string) => ({ length: unescape(encodeURIComponent(s)).length }) };

/** Exact serialized (UTF-8) byte size — the number both caps are measured against. */
export function drawingByteSize(d: Drawing): number {
  return encoder.encode(serializeDrawing(d)).length;
}

export function maxBytesForMode(mode: DrawMode): number {
  return mode === "mini" ? ICON_DRAWING_MAX_BYTES : FULL_DRAWING_MAX_BYTES;
}

export interface SizeCheck {
  bytes: number;
  maxBytes: number;
  /** bytes / maxBytes, clamped to 1 — drives the fill indicator. */
  fraction: number;
  /** true → submit must be blocked. */
  over: boolean;
  /** ≥ 0.85 → warn the player before they hit the wall. */
  nearlyFull: boolean;
}

export function checkDrawingSize(d: Drawing, maxBytes: number): SizeCheck {
  const bytes = drawingByteSize(d);
  const raw = maxBytes > 0 ? bytes / maxBytes : 1;
  return {
    bytes,
    maxBytes,
    fraction: Math.min(1, Math.max(0, raw)),
    over: bytes > maxBytes,
    nearlyFull: raw >= 0.85,
  };
}

/** True when the drawing may be submitted (non-empty and within cap). */
export function canSubmitDrawing(d: Drawing, maxBytes: number): boolean {
  return !isEmptyDrawing(d) && !checkDrawingSize(d, maxBytes).over;
}

// ── edit helpers (all pure, all return a new Drawing) ─────────────────────────

export function addStroke(d: Drawing, stroke: Stroke): Drawing {
  return { w: d.w, h: d.h, strokes: [...d.strokes, stroke] };
}

/** Undo: drop the most recent stroke. */
export function undoStroke(d: Drawing): Drawing {
  if (d.strokes.length === 0) return d;
  return { w: d.w, h: d.h, strokes: d.strokes.slice(0, -1) };
}

/** Clear: drop every stroke, keep the coordinate space. */
export function clearStrokes(d: Drawing): Drawing {
  if (d.strokes.length === 0) return d;
  return { w: d.w, h: d.h, strokes: [] };
}

export function strokeCount(d: Drawing): number {
  return d.strokes.length;
}

export function pointCount(d: Drawing): number {
  let n = 0;
  for (const s of d.strokes) n += Math.floor(s.pts.length / 2);
  return n;
}

export function isEmptyDrawing(d: Drawing | null | undefined): boolean {
  if (!d || !Array.isArray(d.strokes)) return true;
  return !d.strokes.some((s) => s.pts.length >= 2);
}

// DELETED 2026-07-27 (R63): `isValidDrawing()` used to live here — an exported
// structural guard for "drawings arriving from the wire" that nothing on the
// wire path ever called. A validator with no call site is worse than no
// validator: it reads, in review and in a security audit, as a boundary that
// exists. The real boundary is and always was server-side, in
// `src/party/games/drawful.ts`'s `onAction` (0–1000 coords, stroke/point/width
// caps, 250 KB serialized) plus the 300 KB WS frame cap — the client is not a
// trust boundary and must never look like one. If a client-side structural
// check is ever genuinely wanted, wire it at the call site in the same commit.

// ── rendering (shared by DrawingView, PlayerBadge, VoteList, DrawCanvas) ──────

export interface SvgPolyline {
  /** SVG `points` attribute: "x,y x,y …". Empty for a single-point stroke. */
  points: string;
  stroke: string;
  strokeWidth: number;
  /** Single-point strokes (taps) render as a dot instead of a polyline. */
  dot: { x: number; y: number } | null;
}

/**
 * THE conversion every renderer uses. One entry per stroke, in paint order.
 * A one-point stroke yields `dot` (a polyline with a single point draws
 * nothing in SVG, so taps would silently vanish).
 */
export function strokesToSvgPolylines(d: Drawing | null | undefined): SvgPolyline[] {
  if (!d || !Array.isArray(d.strokes)) return [];
  const out: SvgPolyline[] = [];
  for (const s of d.strokes) {
    const n = Math.floor(s.pts.length / 2);
    if (n === 0) continue;
    if (n === 1) {
      out.push({ points: "", stroke: s.c, strokeWidth: s.w, dot: { x: s.pts[0], y: s.pts[1] } });
      continue;
    }
    const parts: string[] = new Array(n);
    for (let i = 0; i < n; i++) parts[i] = `${s.pts[i * 2]},${s.pts[i * 2 + 1]}`;
    out.push({ points: parts.join(" "), stroke: s.c, strokeWidth: s.w, dot: null });
  }
  return out;
}

export function drawingViewBox(d: Drawing | null | undefined): string {
  const w = d && d.w > 0 ? d.w : DRAW_SPACE;
  const h = d && d.h > 0 ? d.h : DRAW_SPACE;
  return `0 0 ${w} ${h}`;
}

// ── § F198 — THE DRAWING AS IT HAPPENED ─────────────────────────────────────
//
// Jake, on Head To Head Doodle: *"the watcher needs to see all of the drawing
// as it happened (no pauses). This includes erasing."* A `Drawing` is only the
// END state, so the pad also emits an EVENT for everything it does, in order,
// with its time (ms since the first event):
//
//   ["s", t, colour, width, x, y]   a stroke starts at (x, y)
//   ["p", t, x, y]                  the current stroke reaches (x, y)
//   ["e", t, x, y, radius]          the eraser rubs out around (x, y)
//   ["u", t]                        undo: the last stroke goes
//
// Folding the events through `applyDrawEvent` rebuilds the drawing exactly,
// because it is the SAME arithmetic the pad runs (`eraseStrokes` is shared).

export type DrawEvent =
  | ["s", number, string, number, number, number]
  | ["p", number, number, number]
  | ["e", number, number, number, number]
  | ["u", number];

export interface DrawingLog {
  v: 1;
  events: DrawEvent[];
}

/**
 * Rub out everything within `radius` of (x, y). Each stroke becomes the RUNS
 * of points that survive, so erasing the middle of a line leaves its two ends;
 * a run of one point is dropped. Returns null when nothing was touched.
 */
export function eraseStrokes(strokes: readonly Stroke[], x: number, y: number, radius: number): Stroke[] | null {
  let changed = false;
  const next: Stroke[] = [];
  for (const stroke of strokes) {
    const reach = radius + stroke.w / 2;
    let run: number[] = [];
    const runs: number[][] = [];
    let touched = false;
    for (let i = 0; i + 1 < stroke.pts.length; i += 2) {
      const px = stroke.pts[i]!;
      const py = stroke.pts[i + 1]!;
      if (Math.hypot(px - x, py - y) <= reach) {
        changed = true;
        touched = true;
        if (run.length >= 4) runs.push(run);
        run = [];
        continue;
      }
      run.push(px, py);
    }
    if (run.length >= 4) runs.push(run);
    if (!touched) next.push(stroke);
    else for (const pts of runs) next.push({ ...stroke, pts });
  }
  return changed ? next : null;
}

const HEX = /^#[0-9a-fA-F]{3,8}$/;

/** Apply one event to a drawing, in place. Anything malformed is ignored. */
export function applyDrawEvent(d: Drawing, ev: readonly unknown[]): void {
  const n = (i: number) => (typeof ev[i] === "number" && Number.isFinite(ev[i]) ? (ev[i] as number) : NaN);
  switch (ev[0]) {
    case "s": {
      const c = typeof ev[2] === "string" && HEX.test(ev[2]) ? ev[2] : "#111111";
      const w = n(3);
      const x = n(4);
      const y = n(5);
      if ([w, x, y].some(Number.isNaN)) return;
      d.strokes.push({ c, w: Math.max(1, Math.min(80, w)), pts: [clampCoord(x), clampCoord(y)] });
      return;
    }
    case "p": {
      const s = d.strokes[d.strokes.length - 1];
      const x = n(2);
      const y = n(3);
      if (!s || Number.isNaN(x) || Number.isNaN(y)) return;
      s.pts.push(clampCoord(x), clampCoord(y));
      return;
    }
    case "e": {
      const x = n(2);
      const y = n(3);
      const r = n(4);
      if ([x, y, r].some(Number.isNaN)) return;
      const next = eraseStrokes(d.strokes, x, y, r);
      if (next) d.strokes = next;
      return;
    }
    case "u":
      d.strokes.pop();
      return;
  }
}

/** Longest gap the replay keeps between two events: no pauses (§ F198). */
export const REPLAY_MAX_GAP_MS = 60;

/**
 * Each event's time ON THE REPLAY: real gaps kept up to `REPLAY_MAX_GAP_MS`,
 * anything longer (thinking, picking a colour, putting the phone down) cut to
 * it. The pen's own speed inside a stroke is preserved; the waiting is not.
 */
export function replayTimes(events: readonly (readonly unknown[])[]): number[] {
  const out: number[] = [];
  let clock = 0;
  let prev: number | null = null;
  for (const ev of events) {
    const t: number = typeof ev[1] === "number" && Number.isFinite(ev[1]) ? (ev[1] as number) : (prev ?? 0);
    if (prev !== null) clock += Math.max(0, Math.min(REPLAY_MAX_GAP_MS, t - prev));
    out.push(clock);
    prev = t;
  }
  return out;
}

/** The final drawing a log produces. */
export function drawingFromLog(log: unknown): Drawing {
  const d = emptyDrawing();
  const events = (log as { events?: unknown })?.events;
  if (!Array.isArray(events)) return d;
  for (const ev of events) if (Array.isArray(ev)) applyDrawEvent(d, ev);
  return d;
}
